import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, Info } from "lucide-react";

/**
 * iter226 — Shared reassessment/binding banner for safety assessments.
 * Consumes GET /api/safety/assessments/{aid}/binding-status.
 *
 * Behaviour:
 *   CURRENT           → renders nothing (safety badge stays authoritative)
 *   OUTDATED_REVISION → amber banner: revision has advanced
 *   OUTDATED_CONTENT  → amber banner: same revision label, content changed
 *   LEGACY_UNVERIFIED → neutral (slate) banner: legacy binding not verifiable
 *   FORMULA_MISSING   → red banner: source formula was removed
 *
 * Information-only per user directive — no reassessment action button.
 */
export default function SafetyBindingBanner({ assessmentId }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!assessmentId) return;
    let cancelled = false;
    api.get(`/safety/assessments/${assessmentId}/binding-status`)
      .then(d => { if (!cancelled) setState(d); })
      .catch(() => { if (!cancelled) setState({ status: "ERROR" }); });
    return () => { cancelled = true; };
  }, [assessmentId]);

  if (!state || state.status === "CURRENT" || state.status === "ERROR") return null;

  if (state.status === "LEGACY_UNVERIFIED") {
    return (
      <div className="flex items-start gap-3 rounded-md border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-testid="safety-binding-banner-legacy">
        <Info size={16} className="mt-0.5 text-slate-500 shrink-0" />
        <div>
          <div className="font-medium">Eski değerlendirme — bağlantı doğrulanamıyor</div>
          <div className="text-xs mt-0.5 text-slate-600">
            Bu eski değerlendirme için formül revizyon bağlantısı doğrulanamıyor.
          </div>
        </div>
      </div>
    );
  }

  if (state.status === "FORMULA_MISSING") {
    return (
      <div className="flex items-start gap-3 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="safety-binding-banner-formula-missing">
        <AlertTriangle size={16} className="mt-0.5 text-red-600 shrink-0" />
        <div>
          <div className="font-medium">Kaynak formül bulunamıyor</div>
          <div className="text-xs mt-0.5 text-red-700">
            Bu değerlendirmenin kaynak formülü sistemde bulunmuyor.
          </div>
        </div>
      </div>
    );
  }

  // OUTDATED_REVISION or OUTDATED_CONTENT → amber banner
  const isRev = state.status === "OUTDATED_REVISION";
  return (
    <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid={isRev ? "safety-binding-banner-outdated-revision" : "safety-binding-banner-outdated-content"}>
      <AlertTriangle size={16} className="mt-0.5 text-amber-600 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium">Güvenlik değerlendirmesi güncel değil</div>
        <div className="text-xs mt-0.5 text-amber-800">
          Güvenlik değerlendirmesi mevcut formül revizyonu ile güncel değil. Yeni değerlendirme yapılması gerekiyor.
        </div>
        {isRev && state.active_revision && state.assessed_revision && (
          <div className="text-[11px] mt-1 text-amber-700">
            Değerlendirme revizyonu: <b>{state.assessed_revision}</b> — aktif revizyon: <b>{state.active_revision}</b>
          </div>
        )}
      </div>
    </div>
  );
}
